import { ApplicationFormTemplate } from '../../../../../../../../entities/v1/applicant_tracking/ApplicationFormTemplate';
import { Question } from '../../../../../../../../entities/v1/survey_engine/Question';
import { SelectOption } from '../../../../../../../../components/Select';
import { Option } from '../../../../../../../../entities/v1/survey_engine/Option';
import { JobService } from '../../../../../../../../services/v1/applicant_tracking/JobService';

export interface QuestionSelectOption extends SelectOption {
  data?: {
    fieldType: string;
    options: Option[];
  };
}

export function getAllQuestionsFromTemplates(
  templates: ApplicationFormTemplate[],
): Question[] {
  return templates.reduce((acc: Question[], template) => {
    const questions =
      template?.surveyTemplate?.sections?.reduce(
        (sectionAcc: Question[], section) => [
          ...sectionAcc,
          ...(section?.questions || []),
        ],
        [],
      ) || [];
    return [...acc, ...questions];
  }, []);
}

export function removeDuplicates(questions: Question[]): Question[] {
  const uniqueQuestions = new Map<string, Question>();

  // Keep only one question per title (the first one encountered)
  questions.forEach((question) => {
    if (question?.title && !uniqueQuestions.has(question.title)) {
      uniqueQuestions.set(question.title, question);
    }
  });

  return Array.from(uniqueQuestions.values());
}

export async function resolveAppliedJobNames(
  appliedJobs: SelectOption[],
): Promise<SelectOption[]> {
  const hasUnresolvedLabels = appliedJobs.some(
    (job) => job.label === job.value,
  );
  if (!hasUnresolvedLabels) return appliedJobs;

  try {
    const ids = appliedJobs
      .map((job) => parseInt(job.value, 10))
      .filter((id) => !isNaN(id));
    const jobList = await JobService.list({ ids, page: 1, pageSize: 100 });

    return appliedJobs.map((job) => {
      const match = jobList.jobs.find((j) => j.id.toString() === job.value);
      return match ? { ...job, label: match.name } : job;
    });
  } catch (error) {
    return appliedJobs;
  }
}

export function buildQuestionJobNames(
  templates: ApplicationFormTemplate[],
  appliedJobs: SelectOption[],
): Map<string, string[]> {
  const jobNamesById = new Map<number, string>(
    appliedJobs.map((job) => [parseInt(job.value, 10), job.label]),
  );
  const jobNamesByQuestionTitle = new Map<string, Set<string>>();

  templates.forEach((template) => {
    const jobNames = (template?.jobIds || [])
      .filter((jobId) => jobNamesById.has(jobId))
      .map((jobId) => jobNamesById.get(jobId));

    if (jobNames.length === 0) return;

    getAllQuestionsFromTemplates([template]).forEach((question) => {
      if (!question?.title) return;

      const names =
        jobNamesByQuestionTitle.get(question.title) || new Set<string>();
      jobNames.forEach((name) => names.add(name));
      jobNamesByQuestionTitle.set(question.title, names);
    });
  });

  return new Map(
    Array.from(jobNamesByQuestionTitle, ([title, names]) => [
      title,
      Array.from(names).sort(),
    ]),
  );
}

function jobNamesTooltipText(jobNames?: string[]): string | undefined {
  if (!jobNames || jobNames.length === 0) return undefined;

  return `Jobs: ${jobNames.join(', ')}`;
}

export function mapQuestionsToSelectOptions(
  questions: Question[],
  selectedValue?: string,
  jobNamesByQuestionTitle?: Map<string, string[]>,
): QuestionSelectOption[] {
  const uniqueQuestions = removeDuplicates(questions);

  return uniqueQuestions
    .filter(
      (question) =>
        question?.fieldType !== null &&
        question?.title &&
        question.isCustomQuestion,
    )
    .map((question) => ({
      value: question.title,
      label: question.title,
      selected: selectedValue === question.title,
      additionalTooltipText: jobNamesTooltipText(
        jobNamesByQuestionTitle?.get(question.title),
      ),
      data: {
        fieldType: question.fieldType,
        options: question.options || [],
      },
    }));
}

export function findQuestionById(
  templates: ApplicationFormTemplate[],
  questionId: string,
): Question | undefined {
  const allQuestions = getAllQuestionsFromTemplates(templates);
  return allQuestions.find(
    (q) => q.id.toString() === questionId && q.isCustomQuestion,
  );
}

export function findQuestionByTitle(
  templates: ApplicationFormTemplate[],
  questionTitle: string,
): Question | undefined {
  const allQuestions = getAllQuestionsFromTemplates(templates);
  // Find the first question with a matching title
  return allQuestions.find(
    (q) => q.title === questionTitle && q.isCustomQuestion,
  );
}
